/**
 * Session State Types
 *
 * Defines the complete state structure for game sessions, including:
 * - Session metadata (quest flags, XP, time, rounds)
 * - Scene state (environment, hazards, character locations)
 *
 * The session state is managed by the SessionStateTool and persisted to:
 * - JSON file during live gameplay (`data/sessions/{session_id}/session_state.json`)
 * - Firebase on session end/pause
 */

// ============================================================================
// Encounter Types
// ============================================================================

/**
 * Encounter types classify what's happening in a scene.
 * - exploration: Default, environment focus (traveling, investigating, no NPC interaction)
 * - social: Non-hostile NPCs present and engaged (conversation, negotiation, trading)
 * - combat: Hostile creatures present (combat is active when encounter_type === 'combat')
 */
export type EncounterType = 'exploration' | 'social' | 'combat';

// Import EncounterTypeEnum from constants for type-safe comparisons
import { EncounterTypeEnum } from '../constants/combat';

/**
 * Helper function to check if combat is active based on encounter_type.
 * This is the single source of truth for combat state.
 * @param encounterType - The current encounter type
 * @returns true if encounter_type is 'combat'
 */
export function isCombatActive(encounterType: EncounterType | undefined): boolean {
  return encounterType === EncounterTypeEnum.COMBAT;
}

// ============================================================================
// Campaign Journal Types
// ============================================================================

/**
 * Campaign Journal tracks important events across the session.
 * Provides context for session recaps, NPC relationships, quests, and discoveries.
 */
export interface CampaignJournal {
  /** Session-by-session recaps */
  session_recaps: SessionRecap[];
  /** Important events that span scenes */
  events: JournalEvent[];
  /** NPC relationship tracking */
  npc_relationships: NPCRelationship[];
  /** Quest/objective tracking */
  quests: QuestEntry[];
  /** Discovered locations, items, secrets */
  discoveries: Discovery[];
  /** Major party decisions and their outcomes */
  decisions: Decision[];
  /** GM-only plot hooks and secrets (not sent to players) */
  gm_notes: GMNote[];
}

/**
 * Session recap summarizing key events from a play session
 */
export interface SessionRecap {
  /** Session number */
  session_number: number;
  /** Round range this session covered */
  round_start: number;
  round_end: number;
  /** Brief summary (1-3 sentences) */
  summary: string;
  /** Key events that happened */
  highlights: string[];
  /** When this recap was created */
  created_at: string;
  /** Optional: Written by player (for XP incentive) */
  author_char_id?: string;
}

/**
 * Journal event tracking important happenings in the campaign
 */
export interface JournalEvent {
  /** Unique event identifier */
  event_id: string;
  /** When it happened */
  round: number;
  scene_id: string;
  /** Classification */
  event_type:
    | 'combat'
    | 'discovery'
    | 'social'
    | 'milestone'
    | 'death'
    | 'level_up'
    | 'experience'
    | 'movement'
    | 'rest'
    | 'travel'
    | 'custom'
    | 'item_received';
  /** Importance for filtering */
  importance: 'minor' | 'major' | 'critical';
  /** Brief description */
  summary: string;
  /** Detailed narrative (optional) */
  details?: string;
  /** Characters involved */
  characters_involved: string[];
  /** Tags for searching */
  tags?: string[];
  /** In-game time when the event occurred */
  game_time?: TimeState;
}

export interface TimeState {
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/** NPC attitude toward the party */
export type NPCAttitude = 'hostile' | 'unfriendly' | 'neutral' | 'friendly' | 'allied';

/**
 * NPC relationship tracking how NPCs feel about the party
 */
export interface NPCRelationship {
  npc_id: string;
  npc_name: string;
  /** Current attitude toward party */
  attitude: NPCAttitude;
  /** Previous attitudes (for tracking changes) */
  attitude_history: {
    attitude: NPCAttitude;
    round: number;
    reason: string;
  }[];
  /** Last interaction summary */
  last_interaction: {
    round: number;
    scene_id: string;
    summary: string;
  };
  /** Notable facts party has learned */
  known_facts: string[];
  /** GM-only: Hidden motivations */
  hidden_motivations?: string[];
}

/** Quest status progression */
export type QuestStatus = 'unbegun' | 'active' | 'completed' | 'failed' | 'abandoned';

/**
 * Quest entry tracking objectives and progress
 */
export interface QuestEntry {
  quest_id: string;
  /** Quest name */
  name: string;
  /** Current status */
  status: QuestStatus;
  /** Quest type */
  quest_type: 'main' | 'side' | 'personal' | 'faction';
  /** Quest giver (if any) */
  quest_giver_id?: string;
  /** Brief description */
  description: string;
  /** Objectives with completion status */
  objectives: {
    description: string;
    completed: boolean;
    completed_round?: number;
  }[];
  /** Rewards (known or discovered) */
  rewards?: {
    xp?: number;
    gold?: number;
    items?: string[];
    reputation?: string;
  };
  /** Round tracking */
  discovered_round: number;
  completed_round?: number;
}

/**
 * Discovery entry for found locations, secrets, items, or lore
 */
export interface Discovery {
  discovery_id: string;
  /** What was discovered */
  discovery_type: 'location' | 'secret' | 'item' | 'lore' | 'ability';
  /** Name/title */
  name: string;
  /** Description */
  description: string;
  /** When/where discovered */
  round: number;
  scene_id: string;
  /** Who discovered it */
  discovered_by: string[];
}

/**
 * Decision entry tracking major party choices
 */
export interface Decision {
  decision_id: string;
  /** What the decision was about */
  context: string;
  /** What the party chose */
  choice: string;
  /** Other options they had */
  alternatives?: string[];
  /** Known consequences so far */
  consequences: string[];
  /** When it happened */
  round: number;
  scene_id: string;
  /** Who was involved in the decision */
  participants: string[];
}

/**
 * GM-only note for tracking plot hooks, secrets, and upcoming events
 */
export interface GMNote {
  note_id: string;
  /** Note category */
  category: 'plot_hook' | 'foreshadowing' | 'secret' | 'upcoming' | 'reminder';
  /** Content */
  content: string;
  /** Related NPCs/locations/quests */
  related_ids?: string[];
  /** When to reveal (optional) */
  reveal_trigger?: string;
  /** When created */
  created_round: number;
  /** Status */
  status: 'active' | 'revealed' | 'obsolete';
}

// ============================================================================
// Root Session State Interface
// ============================================================================

export interface SessionStateData {
  schema_version: '1.0.0';
  session: SessionMetadata;
  scene: SceneState;
}

/**
 * Input type for initializing a new session state.
 * Allows partial nested objects since defaults are provided internally.
 */
export interface InitializeSessionInput {
  session?: Partial<SessionMetadata>;
  scene?: Partial<SceneState>;
}

// ============================================================================
// Session Metadata (Persists across all scenes)
// ============================================================================

export interface SessionMetadata {
  // Core identification
  session_id: string;
  lobby_id: string;
  module_id?: string;

  // Round tracking (both in-game rounds and total elapsed time)
  round: number; // Current round number (increments each round)

  // Scene progression
  scene_history: SceneHistoryEntry[];
  current_scene_id: string;

  // Time tracking (persists across scenes)
  game_time_seconds: number; // Total elapsed game time in seconds
  time_of_day: 'morning' | 'afternoon' | 'evening' | 'night';
  time: TimeState; // Detailed time (day, hour, minute, second)

  // Quest and story progression
  quest_flags: Record<string, boolean>; // Story milestone flags
  story_milestones: string[]; // Ordered list of completed milestones
  module_completed: boolean; // Whether the adventure module has been completed

  // Encounter tracking - encounter_type is the single source of truth for combat state
  /** Current encounter type - exploration (default), social, or combat */
  encounter_type: EncounterType;
  /** Current encounter ID: {scene_id}_{type}_{sequence}. Auto-generated if not provided */
  encounter_id?: string;
  /** History of all encounters in this session. Defaults to empty array */
  encounter_history?: EncounterHistoryEntry[];

  // Rest tracking
  last_short_rest_time?: number; // Game time (seconds) of last short rest
  last_long_rest_time?: number; // Game time (seconds) of last long rest
  short_rests_since_long_rest: number; // Counter for pacing

  // Travel and exploration
  travel_pace?: 'fast' | 'normal' | 'slow'; // Current travel pace
  hours_traveled_today: number; // For forced march tracking

  // Party configuration
  party_default_order: string[]; // Default marching/formation order (char_ids from front to back)

  // Scene snapshots - preserved state for previously visited scenes
  // When party returns to a scene, snapshot is used to restore dead bodies, items, etc.
  // Optional for backwards compatibility - default to empty object
  scene_snapshots?: Record<string, SceneSnapshot>; // scene_id -> snapshot

  // Campaign journal - tracks important events, NPCs, quests, and decisions
  // Optional for backwards compatibility - defaults to empty journal
  campaign_journal?: CampaignJournal;

  // Character IDs of party PCs whose player is away (disconnected past grace).
  // The character stays on the roster; clients render "stepped away".
  away_char_ids?: string[];

  // Extensibility - allows custom fields
  [key: string]: unknown;
}

// ============================================================================
// Scene State (Resets on scene transitions)
// ============================================================================

export interface SceneState {
  // Core identification
  scene_name: string;
  scene_id: string;
  scene_description?: string; // Description of the scene for players

  // Scene media (AI-generated background image/video)
  mediaPath?: string; // GCS path of the generated scene media (e.g., "dynamic/scenes/{sessionId}/scene_123.webp")
  mediaGeneratedAtRound?: number; // Round when the media was last generated (for regeneration tracking)

  // Environment (scene-specific conditions)
  environment: EnvironmentState;

  // Character positioning (scene-specific)
  // NOTE: Location notation is currently free-form strings (e.g., "cave_entrance_left_side", "behind_pillar")
  // TODO: Future enhancement - Support grid-based positioning for scenes with maps
  //       Maps will have grids, characters can be positioned at grid coordinates (e.g., "A5", "B12")
  //       This will require additional scene metadata (map_id, grid_size, grid_type)
  //       and potentially a different location format (grid coordinates vs descriptive strings)
  char_locations: Record<string, string>; // char_id -> location string (free-form or grid coordinate)
  party_member_ids: string[]; // Party members (players + NPCs who joined)
  non_party_char_ids: string[]; // Non-party characters in scene (monsters, location-bound NPCs)

  // Environmental hazards (scene-specific)
  hazards: Hazard[];

  // Active area effects (scene-specific spells like Fog Cloud, Darkness)
  active_effects: ActiveEffect[];

  // Terrain (scene-specific)
  terrain_type: 'normal' | 'difficult';
  terrain_effects: TerrainEffect[];

  // Items in the scene (lootable objects, quest items, treasure)
  // Optional for backwards compatibility - default to empty array
  items?: SceneItem[];

  // Interactable objects (doors, levers, chests, etc.)
  // Optional for backwards compatibility - default to empty array
  interactables?: SceneInteractable[];

  // Combat initiative order - persists across combat rounds, cleared when combat ends
  // Optional for backwards compatibility - default to empty array
  initiative_order?: InitiativeEntry[];

  // Combat round number within current encounter (1, 2, 3...)
  // Distinct from session.round which is the overall game round
  // Reset to 1 when combat starts, incremented each combat round, cleared when combat ends
  // Optional for backwards compatibility - default to undefined (not in combat)
  combat_round?: number;

  // Extensibility - allows custom fields
  [key: string]: unknown;
}

// ============================================================================
// Supporting Types
// ============================================================================

export interface EnvironmentState {
  lighting?: 'bright_light' | 'dim_light' | 'darkness' | 'magical_darkness';
  weather?: 'clear' | 'cloudy' | 'light_rain' | 'heavy_rain' | 'storm' | 'snow' | 'fog' | 'smoke';
  temperature?: 'extreme_cold' | 'cold' | 'normal' | 'hot' | 'extreme_heat' | 'humid';
  // Extensibility - allows custom environment properties
  [key: string]: unknown;
}

export interface InitiativeEntry {
  char_id: string;
  initiative: number; // Initiative roll result
  dex_modifier: number; // Dexterity modifier for tie-breaking
  is_reflexes_turn?: boolean; // Thief's Reflexes (L17): true for bonus turn at initiative-10
}

export interface Hazard {
  id: string; // Unique identifier for this hazard
  type: string; // Type of hazard (e.g., 'fire', 'poison_gas', 'trap')
  location: string; // Where in the scene the hazard is located
  active: boolean; // Whether hazard is currently active
  damage?: string; // Damage expression (e.g., '2d6', '3d10')
  damage_type?: string; // Type of damage (e.g., 'fire', 'poison', 'bludgeoning')
  dc?: number; // Save DC to avoid/reduce damage
  save_type?: string; // Ability for save (e.g., 'dexterity', 'constitution')
}

export interface ActiveEffect {
  effect_id: string; // Unique identifier for this effect
  type: string; // Type of effect (e.g., 'fog_cloud', 'darkness', 'silence')
  area: string; // Area of effect (e.g., '20ft_radius_at_center')
  duration_rounds_remaining: number; // Rounds until effect ends
  source_char_id?: string; // Character who created the effect
  concentration_required: boolean; // Whether maintaining requires concentration
}

export interface TerrainEffect {
  area: string; // Area affected by terrain effect
  type: string; // Type of terrain (e.g., 'difficult_terrain', 'slippery_ice', 'deep_water')
  dc?: number; // Save DC if applicable
  save_type?: string; // Ability for save if applicable
}

export interface SceneHistoryEntry {
  scene_id: string; // Unique scene identifier for snapshot matching
  scene_name: string;
  round_entered: number; // Round when party entered this scene
  round_exited: number | null; // Round when party left (null if current scene)
}

/**
 * History entry for tracking encounters within a session.
 * Encounters represent what's happening in a scene (exploration, social, combat).
 */
export interface EncounterHistoryEntry {
  /** Unique ID: {scene_id}_{type}_{sequence} */
  encounter_id: string;
  /** Type of encounter */
  encounter_type: EncounterType;
  /** Scene where encounter occurred */
  scene_id: string;
  /** Round when encounter began */
  round_started: number;
  /** Round when encounter ended (null if current) */
  round_ended: number | null;
  /** Optional: NPC char_ids involved in this encounter */
  npcs_involved?: string[];
}

// ============================================================================
// Scene Items and Interactables
// ============================================================================

/**
 * An item present in a scene (lootable objects, quest items, etc.)
 */
export interface SceneItem {
  id: string; // Unique item identifier
  name: string; // Display name (e.g., "Rusty Sword", "Healing Potion")
  description?: string; // Optional description
  location: string; // Location in scene (e.g., "on_goblin_body", "treasure_chest")
  lootable: boolean; // Can be picked up by players
  value_gp?: number; // Value in gold pieces (for loot tracking)
  weight_lb?: number; // Weight in pounds (for encumbrance)
  quantity?: number; // Stack size (e.g., 8 coins). Defaults to 1.
  // Extensibility
  [key: string]: unknown;
}

/**
 * Mechanical break config for breakable scene objects.
 * Mirror of `danda_shared::types::module_schema::BreakableConfig`.
 */
export interface BreakableConfig {
  ac: number;
  hp: number;
  set_flag?: string;
  trigger_combat?: boolean;
}

/**
 * An interactable object in a scene (doors, levers, chests, etc.)
 */
export interface SceneInteractable {
  id: string; // Unique identifier
  name: string; // Display name (e.g., "Iron Door", "Lever")
  type: 'door' | 'lever' | 'chest' | 'trap' | 'switch' | 'other';
  location: string; // Location in scene
  state:
    | 'open'
    | 'closed'
    | 'locked'
    | 'activated'
    | 'deactivated'
    | 'triggered'
    | 'looted'
    | 'destroyed';
  locked?: boolean; // For doors/chests
  lock_dc?: number; // DC to pick lock
  trap_triggered?: boolean; // For trapped objects
  /** Mechanical break config (AC/HP/set_flag/trigger_combat). When present,
   *  the FE shows this object in the `/break` slash-command picker. */
  breakable_config?: BreakableConfig;
  /** Remaining HP for breakable objects. None until first hit. */
  current_hp?: number;
  // Extensibility
  [key: string]: unknown;
}

/** Interactable types that semantically support a closed state — gating for
 *  the `/close` slash picker and the closed-state badge in scene UIs. Statues,
 *  corpses, murals, seals, etc. default to `Open` but have no meaningful
 *  closed form. Shared so both FE call sites stay in sync. */
export const CLOSABLE_INTERACTABLE_TYPES = ['door', 'chest', 'lever', 'switch', 'trap'] as const;

// ============================================================================
// Scene Snapshots (Persisted state for revisiting scenes)
// ============================================================================

/**
 * Snapshot of a scene's state when party leaves
 * Used to restore state when party returns to a previously visited scene
 */
export interface SceneSnapshot {
  scene_id: string;
  scene_name: string;
  last_visited_round: number;
  last_visited_game_time: number; // Game time in seconds when party left

  // Spawn tracking flags (prevent re-spawning on revisit)
  npcs_spawned?: boolean; // Whether NPCs have been spawned (defaults to false)
  encounter_triggered?: boolean; // Whether encounter has been triggered (defaults to false)

  // Characters who died in this scene (char_ids)
  // When party returns, these are visible as dead bodies
  dead_char_ids: string[];

  // Items remaining in scene (picked-up items are physically removed)
  items: SceneItem[];

  // State of interactable objects (doors, levers, chests)
  interactables: SceneInteractable[];

  // Environmental changes that persist (triggered traps, etc.)
  triggered_hazards: string[]; // IDs of hazards that were triggered

  // NPCs recruited into the party (joined and haven't left) — skipped during scene restoration
  recruited_char_ids: string[];

  // Path discovery (IDs of hidden paths discovered in this scene)
  discovered_paths?: string[];

  // Extensibility
  [key: string]: unknown;
}

// ============================================================================
// State Changes (Partial updates from Engine output)
// ============================================================================

export interface StateChanges {
  chars?: Record<string, CharacterStateChanges>; // Character state updates
  scene?: SceneStateChanges; // Scene state updates
  session?: SessionStateChanges; // Session state updates
}

export interface CharacterStateChanges {
  hp?: number; // NEW HP value (not delta)
  temp_hp?: number; // NEW temp HP value
  conditions?: {
    add?: string[]; // Conditions to add
    remove?: string[]; // Conditions to remove
  };
  spell_slots?: Record<string, number>; // NEW spell slot counts
  location?: string; // NEW location in scene (see SceneState.char_locations for format notes)
  concentration?: {
    spell?: string;
    duration_seconds?: number;
    is_ritual?: boolean;
  } | null;
  // Extensibility - allows any additional character state fields
  [key: string]: unknown;
}

export interface SceneStateChanges {
  time_increment_seconds?: number; // Seconds to add to session.time
  environment?: Partial<EnvironmentState>; // Partial environment updates
  hazards?: Hazard[]; // NEW hazards array (replaces entire array)
  active_effects?: ActiveEffect[]; // NEW active effects array
  terrain_type?: 'normal' | 'difficult'; // NEW terrain type
  terrain_effects?: TerrainEffect[]; // NEW terrain effects
  initiative_order?: InitiativeEntry[]; // Combat initiative order (persists across combat rounds)
  combat_round?: number; // Combat round within encounter (1, 2, 3...), cleared when combat ends
  // Extensibility - allows any additional scene state fields
  [key: string]: unknown;
}

export interface SessionStateChanges {
  round?: number | '+1'; // NEW round value, or '+1' to increment
  /** NEW encounter type - exploration, social, or combat. This is the source of truth for combat state. */
  encounter_type?: EncounterType;
  quest_flags?: Record<string, boolean>; // Quest flags to merge (union)
  module_completed?: boolean; // Signal that adventure module was completed
  scene_finalized?: boolean; // Signal to trigger scene transition
  session_ended?: boolean; // Signal to end session
  // Extensibility - allows any additional session state fields
  [key: string]: unknown;
}
